import React from 'react'
import { Modal, Button } from 'antd'
import { EyeOutlined, PlusOutlined } from '@ant-design/icons'
import SpinLoader from 'components/SpinLoader'
import _ from 'lodash'
import { injectIntl } from 'react-intl'
import axios from 'axios'
import endpoints from 'services/endpoints'
import { showErrorMessage } from 'services/handleErrors'
import { Amount, getAmountString } from 'components/Amount'
import { qualificationMap } from 'services/utils'
import CommissionsModal from 'components/CommissionsModal'
import RichProgressBar from 'components/RichProgressBar'
import Helper from 'components/Helper'

import style from './style.module.scss'

class IncomeDetails extends React.Component {
  constructor(props) {
    super(props)

    this.initState = {
      user: {},
      income: {},
      loading: false,
      visible: false,
    }

    this.state = {
      ...this.initState,
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (!prevState.visible && this.state.visible) {
      this.getIncomeDetailsByCurrentPeriod()
    }
  }

  getIncomeDetailsByCurrentPeriod = () => {
    this.setState({ loading: true })

    axios
      .post(endpoints.getIncomeDetailsByCurrentPeriod)
      .then(res =>
        this.setState({
          user: _.get(res, 'data.income.user', {}),
          income: _.get(res, 'data.income', {}),
          loading: false,
        }),
      )
      .catch(err => {
        showErrorMessage(err)
        this.setState({ loading: false })
      })
  }

  resetState = () => this.setState({ ...this.initState })

  showModal = () => this.setState({ visible: true })

  closeModal = () => this.setState({ visible: false }, this.resetState)

  renderProgressRows = () => {
    const {
      intl: { formatMessage },
    } = this.props

    const noGoal = (
      <tr>
        <td colSpan={2}>{formatMessage({ id: 'page.partners.details.no-goal' })}</td>
      </tr>
    )

    const { income } = this.state

    if (income && income.goalByIncome) {
      if (!income.goalByIncome.id) {
        return noGoal
      }

      const percentageOfCompletion = income.goalByIncome.percentageOfCompletion
        ? parseInt(income.goalByIncome.percentageOfCompletion, 10)
        : 0

      return (
        <tr>
          <td colSpan={2}>
            <RichProgressBar
              goal={formatMessage({ id: 'page.partners.details.goal-income' }, { amount: getAmountString(income.goalByIncome.expAmount, 'rur') })}
              percent={percentageOfCompletion}
              shortPercentCaption
            />
          </td>
        </tr>
      )
    }

    if (income && income.goalByQualification) {
      const expQualification = income.goalByQualification.expQualification
        ? formatMessage({ id: qualificationMap[income.goalByQualification.expQualification] })
        : null

      const percentageOfCompletion = income.goalByQualification.percentageOfCompletion
        ? parseInt(income.goalByQualification.percentageOfCompletion, 10)
        : 0

      return (
        <tr>
          <td colSpan={2}>
            <RichProgressBar
              goal={formatMessage({ id: 'page.partners.details.goal-qualification' }, { expQualification })}
              percent={percentageOfCompletion}
              shortPercentCaption
            />
          </td>
        </tr>
      )
    }

    return noGoal
  }

  renderGoalByQualification = () => {
    if (!this.state.income.goalByQualification) return null

    const {
      intl: { formatMessage },
    } = this.props
    const personalSales = this.state.income.personalSales || 0
    const personalSalesRequired = this.state.income.goalByQualification.personalSalesRequired || 0
    const personalSalesLeft = personalSalesRequired > 0 ? personalSalesRequired - personalSales : 0
    const incomeSecondToFifthLevel = this.state.income.incomeSecondToFifthLevel || 0
    const incomeSecondToFifthLevelRequired =
      this.state.income.goalByQualification.incomeSecondToFifthLevelRequired || 0
    const incomeSecondToFifthLevelLeft = incomeSecondToFifthLevelRequired - incomeSecondToFifthLevel

    return (
      <>
        <tr>
          <td>
            {formatMessage({ id: 'page.partners.details.personal-sales' })}
            <Helper>
              {formatMessage({ id: 'page.partners.details.personal-sales-description' })}
            </Helper>
          </td>
          <td>
            {personalSales || 0} {formatMessage({ id: 'page.partners.details.out-of' })}{' '}
            {personalSalesRequired || 0}
            <Helper>
              {personalSalesLeft
                ? formatMessage(
                    { id: 'page.partners.details.personal-sales-left' },
                    { personalSalesLeft },
                  )
                : ''}
            </Helper>
            <CommissionsModal
              filter={{ userId: this.state.user.id, isPersonalSales: true }}
              title={<EyeOutlined />}
              buttonProps={{ shape: 'circle' }}
              type="link"
              className={style.showIncomeDetails}
            />
          </td>
        </tr>
        <tr>
          <td>
            {formatMessage({ id: 'page.partners.details.income-second-to-fifth-level' })}
            <Helper>
              {formatMessage({
                id: 'page.partners.details.income-second-to-fifth-level-description',
              })}
            </Helper>
          </td>
          <td>
            <Amount value={incomeSecondToFifthLevel} currencyCode="rur" />
            &nbsp;
            {formatMessage({ id: 'page.partners.details.out-of' })}&nbsp;
            <Amount value={incomeSecondToFifthLevelRequired} currencyCode="rur" />
            <Helper>
              {formatMessage(
                { id: 'page.partners.details.income-second-to-fifth-level-left' },
                { value: getAmountString(incomeSecondToFifthLevelLeft, 'rur') },
              )}
            </Helper>
          </td>
        </tr>
      </>
    )
  }

  renderCommonRows = () => {
    const {
      intl: { formatMessage },
    } = this.props

    const {
      goalByQualification,
      amount,
      personalAmount,
      incomeSecondToFifthLevel,
      personalSales,
      personalTurnover,
      groupTurnover,
    } = this.state.income

    const lastQualification = _.get(this.state, 'income.lastQualification', null)
    const goalNotByQualification = goalByQualification === undefined

    return (
      <>
        <tr>
          <td>
            {formatMessage({ id: 'page.partners.details.approved-qualification' })}
            <Helper>
              {formatMessage({ id: 'page.partners.details.approved-qualification-description' })}
            </Helper>
          </td>
          <td>{lastQualification && formatMessage({ id: qualificationMap[lastQualification] })}</td>
        </tr>
        <tr>
          <td>
            {formatMessage({ id: 'page.partners.details.income' })}
            <Helper>{formatMessage({ id: 'page.partners.details.income-description' })}</Helper>
          </td>
          <td>
            <Amount value={amount} currencyCode="rur" />
            <CommissionsModal
              filter={{ userId: this.state.user.id }}
              title={<EyeOutlined />}
              buttonProps={{ shape: 'circle' }}
              type="link"
              className={style.showIncomeDetails}
            />
          </td>
        </tr>
        <tr>
          <td>
            {formatMessage({ id: 'page.partners.details.first-line-income' })}
            <Helper>{formatMessage({ id: 'page.partners.details.first-line-income-desc' })}</Helper>
          </td>
          <td>
            <Amount value={personalAmount} currencyCode="rur" />
          </td>
        </tr>
        {goalNotByQualification && (
          <>
            <tr>
              <td>
                {formatMessage({ id: 'page.partners.details.second-to-fifth-level-income' })}
                <Helper>
                  {formatMessage({ id: 'page.partners.details.second-to-fifth-level-income-desc' })}
                </Helper>
              </td>
              <td>
                <Amount value={incomeSecondToFifthLevel} currencyCode="rur" />
              </td>
            </tr>
            <tr>
              <td>
                {formatMessage({ id: 'page.partners.details.personale-sales' })}
                <Helper>
                  {formatMessage({ id: 'page.partners.details.personale-sales-desc' })}
                </Helper>
              </td>
              <td>
                {personalSales || 0}
                <CommissionsModal
                  filter={{ userId: this.state.user.id, isPersonalSales: true }}
                  title={<EyeOutlined />}
                  buttonProps={{ shape: 'circle' }}
                  type="link"
                  className={style.showIncomeDetails}
                />
              </td>
            </tr>
          </>
        )}
        <tr>
          <td>
            {formatMessage({ id: 'page.partners.details.personal-turnover' })}
            <Helper>{formatMessage({ id: 'page.partners.details.personal-turnover-desc' })}</Helper>
          </td>
          <td>
            <Amount value={personalTurnover} currencyCode="rur" />
          </td>
        </tr>
        <tr>
          <td>
            {formatMessage({ id: 'page.partners.details.group-turnover' })}
            <Helper>{formatMessage({ id: 'page.partners.details.group-turnover-desc' })}</Helper>
          </td>
          <td>
            <Amount value={groupTurnover} currencyCode="rur" />
          </td>
        </tr>
      </>
    )
  }

  render() {
    const {
      intl: { formatMessage },
    } = this.props
    const { visible, loading } = this.state

    return (
      <>
        <Button onClick={this.showModal} icon={<PlusOutlined />}>
          {formatMessage({ id: 'page.dashboard.goal.more-details' })}
        </Button>
        <Modal
          className={style.modal}
          title={formatMessage({ id: 'page.dashboard.goal.business-performance' })}
          visible={visible}
          footer={null}
          onCancel={this.closeModal}
          afterClose={this.closeModal}
        >
          {loading && <SpinLoader style={{ marginTop: 20 }} />}
          {!loading && visible && (
            <table>
              <tbody>
                {this.renderProgressRows()}
                {this.renderGoalByQualification()}
                {this.renderCommonRows()}
              </tbody>
            </table>
          )}
        </Modal>
      </>
    )
  }
}

export default injectIntl(IncomeDetails)
